import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { IconButton, Stack, Typography } from "@mui/material";
import { X, Copy } from "lucide-react";
import { Customer } from "@/types/customer-type";
import { getInitials } from "./utils/customer-utils";
import { useToast } from "@/hooks/use-toast";

interface CustomerHeaderProps {
  customer: Customer;
  onClear: () => void;
}

export function CustomerHeader({ customer, onClear }: CustomerHeaderProps) {
  const { toast } = useToast();

  const handleCopyEmail = async (email: string) => {
    try {
      await navigator.clipboard.writeText(email);
      toast({
        title: "Copied",
        description: "Email copied to clipboard",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to copy email",
        variant: "destructive",
      });
    }
  };

  return (
    <Stack direction="row" spacing={2} alignItems="center">
      <Avatar className="h-12 w-12">
        {customer.profileImageUrl ? (
          <AvatarImage src={customer.profileImageUrl} alt={customer.name} />
        ) : null}
        <AvatarFallback>{getInitials(customer.name)}</AvatarFallback>
      </Avatar>
      <Stack spacing={0.5} flex={1}>
        <Typography variant="subtitle1" fontWeight={500}>
          {customer.name}
        </Typography>
        {customer.email ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              {customer.email}
            </Typography>
            <IconButton
              size="small"
              onClick={() => handleCopyEmail(customer.email!)}
              sx={{ width: 20, height: 20 }}
            >
              <Copy size={14} />
            </IconButton>
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {customer.phone || "No contact information"}
          </Typography>
        )}
      </Stack>
      <IconButton size="small" color="primary" onClick={onClear}>
        <X size={16} />
      </IconButton>
    </Stack>
  );
}
